/**
 * 要传给后端的query
 * @see https://km.sankuai.com/page/33786745
 */
import Cookies from 'js-cookie'
import { getURLSearchParams, toUrlParams } from '../base/url'
import Agent from '../agent/index'
import { I_AGENT } from '../agent/typing'
import User from '../user'
import { E_ProcessEnv, getProcessEnv } from '../base/env'
import { concurrencyGetLocation, Coords } from '../gis/geo'

function getPhxAppnm(agent: I_AGENT) {
  if (agent.appnm && agent.appnm.indexOf('phoenix') !== -1) {
    // 美团民宿来源：包括美团民宿App、浏览器访问
    return 'phoenix'
  }
  return agent.appnm || 'phoenix'
}

function getPhxPlat(agent: I_AGENT) {
  const phxAppnm = getPhxAppnm(agent)
  let phxPlat = 'i'
  if (phxAppnm === 'phoenix') {
    if (agent.anyminiprogram) {
      phxPlat = 'xiaochengxu'
    } else if (agent.iphone) {
      phxPlat = 'iphone'
    } else if (agent.android) {
      phxPlat = 'android'
    } else if (agent.pc) {
      phxPlat = 'www'
    }
    return phxPlat
  }
  if (agent.native || agent.anyminiprogram) {
    if (agent.iphone) {
      phxPlat = 'iphone'
    } else if (agent.android) {
      phxPlat = 'android'
    }
  }
  return phxPlat
}

function getDefaultQueryParams(agent: I_AGENT) {
  let params = {
    phx_appnm: getPhxAppnm(agent),
    phx_app_version: agent.version || 1, // 领券必须,不能为空
    phx_plat: getPhxPlat(agent),
    uuid: User.uuid,
    phx_is_hybrid: 1,
    cityid: Cookies.get('cityid'), // 不同 app 定义不同
  }
  if (Agent.msc && Agent.harmony) {
    params.ddcp_source = `harmony~${Agent.meituan ? 10 : 1}~msc`
  }
  return params
}

const requestQuery = [
  /* 灵犀相关的参数也汇总在此处 */
  'phx_appnm',
  'phx_app_version',
  'phx_plat',
  'uuid',
  'phx_channel',
  'phx_sub_channel',
  'phx_is_hybrid',
  'phx_geo_city_id',
  'phx_wake_up_type',
  'phx_wake_up_source',
  'phx_ad_delivery_id',
  /* 直播透传 */
  'liveid',
  /* 鸿蒙系统特有参数 */
  'ddcp_source',
]

export const INVITE_QUERY_KEY = 'phx_ucode'

export function getInviteCode(srcUrl = window.location.href): string | null | undefined {
  if (
    Number(getProcessEnv(E_ProcessEnv.VUE_APP_ACTIVITY_ID)) === 2129 ||
    Number(getProcessEnv(E_ProcessEnv.VUE_APP_ACTIVITY_ID)) === 6
  ) {
    const srcURL = new URL(srcUrl)
    // 旧的 URL 不存在 URLSearchParams，需要兼容
    let srcSearchParams = srcURL.searchParams
    if (!srcSearchParams) {
      srcSearchParams = getURLSearchParams(srcURL.search)
    }
    // code 会跟微信的参数重名，仅为历史参数
    let inviteCode = srcSearchParams.get(INVITE_QUERY_KEY) || srcSearchParams.get('code')
    if (!inviteCode) {
      // 该参数约定为 pinv=userid_inviteCode
      const pinv = srcSearchParams.get('pinv')
      const tempMatches = /^(\d+)_([a-zA-Z0-9]+)$/.exec(pinv || '')
      inviteCode = (tempMatches && tempMatches[2]) || ''
    }
    return inviteCode
  }
  return undefined
}

/**
 * [getRequestParams 获取添加埋点参数的]
 * @return {[URLSearchParams]}  [添加的埋点的params]
 */
export function getRequestParams(srcUrl: string, agent: I_AGENT) {
  const srcURL = new URL(srcUrl)
  // 旧的 URL 不存在 URLSearchParams，需要兼容
  let srcSearchParams = srcURL.searchParams
  if (!srcSearchParams) {
    srcSearchParams = getURLSearchParams(srcURL.search)
  }
  const destSearchParams = new URLSearchParams()
  // sso 用
  destSearchParams.set('phx_act_id', String(getProcessEnv(E_ProcessEnv.VUE_APP_ACTIVITY_ID)))

  const setInviteCode = function () {
    const inviteCode = getInviteCode(srcUrl)
    if (inviteCode) {
      destSearchParams.set('phx_invite_code', inviteCode)
    }
  }
  setInviteCode()

  const setByKey = function (dest: string, src: string, process: Function = (value: string) => value) {
    const value = srcSearchParams.get(src)
    if (value) {
      destSearchParams.set(dest, process(value))
    }
  }

  const defaultQueryParams: Record<string, string | number | undefined> = getDefaultQueryParams(agent)
  for (const queryKey of requestQuery) {
    if (defaultQueryParams[queryKey]) {
      destSearchParams.set(queryKey, String(defaultQueryParams[queryKey]))
    }
    const queryValue = srcSearchParams.get(queryKey)
    if (queryValue) {
      destSearchParams.set(queryKey, queryValue)
    } else if (queryKey === 'phx_app_version') {
      setByKey(queryKey, agent.anyminiprogram ? 'mina_version' : 'version_name')
    } else if (queryKey === 'phx_geo_city_id') {
      setByKey(queryKey, 'locCityId')
    }
  }
  return destSearchParams
}

export default getRequestParams(window.location.href, Agent)

export async function withLocation(url: string) {
  const coords: Coords = await new Promise((resolve) => {
    concurrencyGetLocation((res: Coords) => {
      resolve(res)
    })
  })
  const gis = toUrlParams({
    phx_lat: coords.lat,
    phx_lng: coords.lng,
  })
  url += (url.indexOf('?') !== -1 ? '' : '?') + gis
  return url
}
